import React from "react";
import type { QuickLoginRow } from "@observe-tech/utils";
import { useTranslation } from "react-i18next";
import type { Step } from "../../useLoginSteps";
import type {
  QuickLoginFlow,
  QuickLoginStep,
} from "../hooks/useQuickLoginFlow";
import { ChoosePin } from "./ChoosePin";
import { LinkButton } from "./LinkButton";
import { PinLogin } from "./PinLogin";
import { QuickLoginPrompt } from "./QuickLoginPrompt";
import { SavedAccountPassword } from "./SavedAccountPassword";
import { SavedAccounts } from "./SavedAccounts";

export interface QuickLoginStepsProps {
  step: Step;
  rows: QuickLoginRow[];
  online: boolean;
  flow: QuickLoginFlow;
  goTo(step: QuickLoginStep | "forgot-request"): void;
  onLoginForm(): void;
  onPasswordSubmit(email: string, password: string): void;
}

export const QuickLoginSteps: React.FC<QuickLoginStepsProps> = ({
  step,
  rows,
  online,
  flow,
  goTo,
  onLoginForm,
  onPasswordSubmit,
}) => {
  const { t } = useTranslation();
  const { pin } = flow;
  const account = pin.account;
  const hasAccounts = rows.length > 0;

  return (
    <>
      {(step === "login" || step === "pin" || step === "account-password") &&
        hasAccounts && (
          <div className="w-[300px]">
            <LinkButton onClick={() => goTo("accounts")} disabled={pin.pending}>
              {t("Back to saved accounts")}
            </LinkButton>
          </div>
        )}
      {step === "accounts" && (
        <SavedAccounts
          rows={rows}
          online={online}
          onSelect={flow.selectAccount}
          onForget={(row) => flow.forgetAccount(row, false)}
          onDifferentEmail={onLoginForm}
        />
      )}
      {step === "pin" && account && (
        <PinLogin
          email={account.email}
          value={pin.value}
          onChange={pin.change}
          error={pin.error}
          disabled={pin.disabled}
          online={online}
          onPassword={() => goTo("account-password")}
          onForget={() => flow.forgetAccount(account, true)}
        />
      )}
      {step === "account-password" && account && (
        <SavedAccountPassword
          email={account.email}
          online={online}
          onSubmit={(password) => onPasswordSubmit(account.email, password)}
          onForgotPassword={() => goTo("forgot-request")}
          onDifferentEmail={onLoginForm}
        />
      )}
      {step === "enrol-prompt" && (
        <QuickLoginPrompt
          onSetUp={() => goTo("pin-choose")}
          onNotNow={flow.skip}
          disabled={flow.enrolmentPending}
        />
      )}
      {step === "pin-choose" && (
        <ChoosePin
          onComplete={flow.enrol}
          onSkip={flow.skip}
          disabled={flow.enrolmentPending}
        />
      )}
    </>
  );
};
